// CMP-0103 · LFS Footer (SEO link columns, owner actions, Housal credit) · Catalog Engine · Lot Discovery · owner: Platform
// 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
// 99-1-decision: ADAPT the approved Concept D "Footer D" prototype into one shared React footer (registry SEL-LFS-001); link columns fed by live inventory counts
"use client"

import Link from "next/link"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Icon, Logo } from "./primitives"
import { INQUIRE, ROUTES } from "@/lib/lfs/catalog"
import type { NavData } from "@/lib/lfs/nav"

type FootLink = { label: string; href: string; more?: boolean }

const actionBtn = { display: "inline-flex", alignItems: "center", gap: 8, height: 46, padding: "0 14px", borderRadius: 10, color: "#fff", textDecoration: "none", fontSize: 14, fontWeight: 600 } as const

export function LfsFooter({ nav }: { nav: NavData }) {
  const [open, setOpen] = useState<string | null>(null)
  const groups: [string, string, FootLink[]][] = [
    ["type", "Lots by Type", [...nav.types.map((t) => ({ label: `${t.label} Lots`, href: t.href })), { label: "All Available Lots", href: ROUTES.lots, more: true }]],
    ["city", "Lots by City", [...nav.cities.slice(0, 6).map((c) => ({ label: `Lots for Sale in ${c.name}`, href: c.href })), { label: "View All Cities", href: `${ROUTES.locations}#places`, more: true }]],
    ["vil", "Popular Villages", [...nav.villages.slice(0, 5).map((v) => ({ label: `Lots for Sale in ${v.name}`, href: v.href })), { label: "View All Villages", href: ROUTES.villages, more: true }]],
    ["dev", "Land Developments", [...nav.projects.slice(0, 5).map((p) => ({ label: `Lots for Sale in ${p.name}`, href: p.href })), { label: "View All Projects", href: ROUTES.developments, more: true }]],
    ["own", "For Lot Owners", [{ label: "List Your Lot for Sale", href: INQUIRE.sell, more: true }, { label: "How brokerage listing works", href: `${ROUTES.about}#how` }, { label: "Brokerage disclosures", href: ROUTES.disclosures }]],
    ["res", "Resources & Information", [
      // 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
      // 99-1-decision: link only published guides; the step-by-step guide has no text yet, so this opens the guides list
      { label: "Land Buying Guides", href: "/insights" },
      { label: "Price per Square Meter Guide", href: "/insights/price-per-sqm" },
      { label: "FAQs", href: "/#faq" },
      { label: "About", href: ROUTES.about },
      { label: "Contact Us", href: INQUIRE.general },
      { label: "Privacy Policy", href: ROUTES.privacy },
      { label: "Terms of Use", href: ROUTES.terms },
    ]],
  ]

  return (
    <footer style={{ background: "#121722", color: "#cbd5e1" }}>
      <div style={{ padding: "clamp(36px,6vw,56px) var(--lfs-gutter) 28px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "20px 48px", justifyContent: "space-between", alignItems: "flex-start", paddingBottom: 32, borderBottom: "1px solid #2A3140" }}>
          <div style={{ flex: "1 1 300px", maxWidth: 420 }}>
            <Logo dark />
            <p style={{ margin: "14px 0 0", fontSize: 14, lineHeight: 1.55, color: "#a9b6c6", textWrap: "pretty" }}>
              Residential, commercial, industrial and agricultural lots across the Philippines, handled by licensed real estate brokers.
            </p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <a href={INQUIRE.sell} className="lfs-btn-accent" style={actionBtn}><Icon name="add_home" size={20} />List Your Lot for Sale</a>
            <a href={INQUIRE.general} style={{ ...actionBtn, border: "1px solid #3A4352" }}><Icon name="forum" size={20} />Contact us</a>
            <Link href={ROUTES.locations} style={{ ...actionBtn, border: "1px solid #3A4352" }}><Icon name="travel_explore" size={20} />Browse all locations</Link>
          </div>
        </div>

        <div className="lfs-foot-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,190px),1fr))", gap: "0 32px" }}>
          {groups.map(([id, title, links]) => (
            <nav key={id} aria-label={title} className="lfs-foot-group" data-open={open === id}>
              <Button
                variant="ghost"
                type="button"
                className="lfs-foot-head"
                aria-expanded={open === id}
                onClick={() => setOpen(open === id ? null : id)}
                style={{ width: "100%", height: "auto", padding: 0, border: 0, borderRadius: 0, background: "transparent", justifyContent: "space-between", font: "inherit", color: "#fff", textAlign: "left" }}
              >
                <h2 style={{ margin: 0, fontSize: 13, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "#fff" }}>{title}</h2>
                <span className="lfs-foot-chev"><Icon name={open === id ? "remove" : "add"} color="#94a3b8" /></span>
              </Button>
              <div className="lfs-foot-list" style={{ flexDirection: "column", gap: 2 }}>
                {links.map((l) => (
                  <a key={l.label} href={l.href} className="lfs-hover-under" style={{ padding: "6px 0", fontSize: 14, lineHeight: 1.4, color: l.more ? "#fff" : "#cbd5e1", fontWeight: l.more ? 600 : 400, textDecoration: "none" }}>
                    {l.label}
                  </a>
                ))}
              </div>
            </nav>
          ))}
        </div>

        <div style={{ marginTop: 36, paddingTop: 20, borderTop: "1px solid #2A3140", display: "flex", flexWrap: "wrap", gap: "10px 24px", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", lineHeight: 1.5 }}>© {new Date().getFullYear()} LOT FOR SALE PHILIPPINES. All rights reserved.</p>
          <a href="https://housal.com" target="_blank" rel="noopener" className="lfs-hover-under" style={{ fontSize: 12, color: "#94a3b8", textDecoration: "none" }}>Powered by Housal.com</a>
        </div>
      </div>
    </footer>
  )
}
